import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, Shield, Zap, Award, Star, CheckCircle, AlertTriangle } from 'lucide-react';
import PricingSection from '../components/PricingSection';

/*
 * Highlighted chunk of the student's handwritten answer.
 * Renders a phrase with a semi-transparent purple highlighter background,
 * mimicking a hand-drawn marker stroke.
 */
function Highlight({ children, className = '' }) {
    return (
        <span
            className={`relative inline px-1 ${className}`}
            style={{
                background: 'linear-gradient(180deg, transparent 18%, rgba(167,139,250,0.42) 18%, rgba(167,139,250,0.42) 90%, transparent 90%)',
                boxDecorationBreak: 'clone',
                WebkitBoxDecorationBreak: 'clone',
            }}
        >
            {children}
        </span>
    );
}

export default function LandingPage() {
    return (
        <div
            className="min-h-screen overflow-hidden relative"
            style={{
                background: 'linear-gradient(160deg, #EEE7FF 0%, #F5F0FF 45%, #F0E9FF 100%)',
            }}
        >
            {/* Soft ambient glow behind the paper */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute top-[12%] right-[-10%] w-[900px] h-[900px] rounded-full opacity-40"
                style={{
                    background: 'radial-gradient(closest-side, rgba(167,139,250,0.35), transparent 70%)',
                }}
            />

            {/* Header */}
            <header className="relative z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="logo-link">
                            <div className="w-10 h-10 rounded-xl bg-[#7C3AED] flex items-center justify-center shadow-sm">
                                <BookOpen className="w-5 h-5 text-white" strokeWidth={2.5} />
                            </div>
                            <span className="text-xl font-black text-zinc-950 tracking-tight">A-Level AI Grader</span>
                        </Link>
                        <div className="flex items-center gap-5">
                            <a
                                href="#pricing"
                                className="hidden sm:block text-base font-semibold text-zinc-900 hover:text-[#7C3AED] transition-colors"
                                data-testid="header-pricing-link"
                            >
                                Pricing
                            </a>
                            <Link
                                to="/login"
                                className="text-base font-semibold text-zinc-900 hover:text-[#7C3AED] transition-colors"
                                data-testid="header-login-link"
                            >
                                Sign In
                            </Link>
                            <Link
                                to="/register"
                                className="inline-flex items-center text-base font-bold text-white bg-[#7C3AED] hover:bg-[#6D28D9] px-7 py-3 rounded-full transition-colors shadow-[0_4px_14px_-4px_rgba(124,58,237,0.5)]"
                                data-testid="header-register-link"
                            >
                                Get Started
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative z-10 pt-4 pb-10 lg:pt-8 lg:pb-14">
                <div className="max-w-[1380px] mx-auto px-4 sm:px-6 lg:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-10 lg:gap-8 items-center">
                        {/* ========== LEFT: copy + CTAs ========== */}
                        <div className="max-w-2xl">
                            <div
                                className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#EDE4FF] text-[12px] font-bold text-[#7C3AED] tracking-[0.14em] mb-8 uppercase"
                                data-testid="hero-pill"
                            >
                                Pearson Edexcel A-Level
                            </div>
                            <h1 className="text-[56px] sm:text-[68px] lg:text-[84px] font-black tracking-[-0.03em] leading-[0.98] mb-8">
                                <span className="text-zinc-950">Get AI-Powered</span>
                                <br />
                                <span className="text-[#7C3AED]">Exam Feedback</span>
                            </h1>
                            <p className="text-xl text-zinc-700 mb-10 leading-relaxed max-w-xl">
                                Submit your answers, get instant feedback, and learn exactly what you need to improve. Perfect for Business, Economics, and Geography A-Level students.
                            </p>
                            <div className="flex flex-wrap gap-4 mb-12">
                                <Link
                                    to="/register"
                                    className="inline-flex items-center gap-3 text-lg font-bold text-white bg-[#7C3AED] hover:bg-[#6D28D9] px-10 py-5 rounded-full transition-all shadow-[0_6px_20px_-6px_rgba(124,58,237,0.55)] hover:shadow-[0_8px_24px_-6px_rgba(124,58,237,0.7)]"
                                    data-testid="hero-get-started-button"
                                >
                                    Start Free <ArrowRight className="w-5 h-5" strokeWidth={3} />
                                </Link>
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-2 text-lg font-bold text-zinc-950 bg-white border-2 border-zinc-950 hover:bg-zinc-50 px-10 py-5 rounded-full transition-colors shadow-[3px_3px_0_0_rgba(9,9,11,1)] hover:shadow-[2px_2px_0_0_rgba(9,9,11,1)] hover:translate-x-[1px] hover:translate-y-[1px]"
                                    data-testid="hero-sign-in-button"
                                >
                                    Sign In
                                </Link>
                            </div>
                            <div className="flex flex-wrap gap-x-10 gap-y-3 text-[15px] font-semibold text-zinc-700">
                                <span className="flex items-center gap-2">
                                    <Shield className="w-5 h-5 text-[#7C3AED]" strokeWidth={2.5} />
                                    Secure &amp; Private
                                </span>
                                <span className="flex items-center gap-2">
                                    <Zap className="w-5 h-5 text-[#7C3AED]" strokeWidth={2.5} />
                                    Instant Feedback
                                </span>
                                <span className="flex items-center gap-2">
                                    <Award className="w-5 h-5 text-[#7C3AED]" strokeWidth={2.5} />
                                    A-Level Expert Trained
                                </span>
                            </div>
                        </div>

                        {/* ========== RIGHT: paper illustration ========== */}
                        <div className="relative hidden lg:block h-[760px]" data-testid="hero-illustration">
                            {/* The paper (tilted) */}
                            <div
                                className="absolute top-0 left-4 w-[620px] bg-white rounded-xl shadow-[0_30px_80px_-20px_rgba(99,51,186,0.35)]"
                                style={{
                                    transform: 'rotate(6deg)',
                                    backgroundImage:
                                        'repeating-linear-gradient(to bottom, transparent 0, transparent 44px, #E8E8EC 44px, #E8E8EC 45px)',
                                    backgroundSize: '100% 45px',
                                    backgroundPosition: '0 70px',
                                    padding: '50px 48px 76px 48px',
                                }}
                            >
                                {/* Question */}
                                <p className="text-[15px] text-zinc-900 leading-[45px] mb-2 font-medium">
                                    <span className="text-zinc-500 mr-1">(c)</span>
                                    Discuss the extent to which globalisation has benefited businesses.
                                    <span className="text-zinc-500 ml-2">(25 marks)</span>
                                </p>

                                {/* Handwritten-style answer */}
                                <div
                                    className="text-zinc-900 leading-[45px] text-[26px]"
                                    style={{ fontFamily: "'Caveat', cursive", fontWeight: 600 }}
                                >
                                    <p>
                                        <Highlight>Globalisation has created significant</Highlight>
                                        <br />
                                        <Highlight>opportunities for businesses to expand</Highlight>
                                        <br />
                                        <Highlight>into new markets, achieve economies</Highlight>
                                        <br />
                                        <Highlight>of scale and access cheaper resources.</Highlight>
                                    </p>
                                    <p className="mt-1 relative">
                                        <Highlight>This has led to increased efficiency</Highlight>
                                        <br />
                                        <span className="relative">
                                            <Highlight>and profitability for many firms.</Highlight>
                                            <svg
                                                className="inline-block ml-2 -translate-y-1"
                                                width="26"
                                                height="26"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                            >
                                                <path
                                                    d="M4 12 L10 18 L20 6"
                                                    stroke="#7C3AED"
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </p>
                                    <p className="mt-1">
                                        However, globalisation has also increased
                                        <br />
                                        competition, putting pressure on profit
                                        <br />
                                        margins and leading to <Highlight>job losses in</Highlight>
                                        <br />
                                        <Highlight>certain industries.</Highlight>
                                    </p>
                                    <p className="mt-1">
                                        Overall, while the <Highlight>benefits can be</Highlight>
                                        <br />
                                        <Highlight>substantial, they are not experienced</Highlight>
                                        <br />
                                        <Highlight>equally by all businesses.</Highlight>
                                    </p>
                                </div>

                                {/* Purple right curly brace on bottom-right section */}
                                <svg
                                    className="absolute right-[-8px] bottom-[36px]"
                                    width="34"
                                    height="200"
                                    viewBox="0 0 28 170"
                                    fill="none"
                                    preserveAspectRatio="none"
                                >
                                    <path
                                        d="M4 4 C 18 4, 18 40, 18 60 C 18 75, 24 82, 26 85 C 24 88, 18 95, 18 110 C 18 130, 18 166, 4 166"
                                        stroke="#7C3AED"
                                        strokeWidth="2.5"
                                        fill="none"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </div>

                            {/* ========== Floating cards ========== */}

                            {/* SCORE – top right */}
                            <div
                                className="absolute top-[48px] right-[-20px] w-[220px] bg-white rounded-2xl p-6 shadow-[0_12px_40px_-10px_rgba(99,51,186,0.3)] border border-zinc-100 z-20"
                                data-testid="score-card"
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-[12px] font-bold text-zinc-500 uppercase tracking-wider">Score</span>
                                    <Star className="w-5 h-5 text-[#7C3AED] fill-[#7C3AED]" />
                                </div>
                                <p className="text-[54px] font-black leading-none mt-3 mb-4">
                                    <span className="text-[#7C3AED]">18</span>
                                    <span className="text-zinc-300 text-[32px] font-bold">/25</span>
                                </p>
                                <div className="h-px bg-zinc-200 mb-3" />
                                <p className="text-[13px] text-zinc-600 leading-snug">
                                    Good understanding
                                    <br />
                                    Strong analysis
                                </p>
                            </div>

                            {/* STRENGTH – left middle */}
                            <div
                                className="absolute top-[290px] left-[-40px] w-[248px] bg-white rounded-2xl p-5 shadow-[0_12px_40px_-10px_rgba(16,185,129,0.25)] border border-zinc-100 z-20"
                                data-testid="strength-card"
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                                        <CheckCircle className="w-3 h-3 text-white" fill="currentColor" />
                                    </div>
                                    <span className="text-[13px] font-bold text-emerald-600">Strength</span>
                                </div>
                                <p className="text-[19px] font-black text-zinc-950 mb-1">Strong AO2</p>
                                <p className="text-[13px] text-zinc-500 leading-snug">
                                    Analysis is clear and
                                    <br />
                                    well-developed.
                                </p>
                                {/* Anchor dot */}
                                <span className="absolute -right-1.5 top-1/2 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white" />
                            </div>

                            {/* EVALUATION NEEDED – right middle */}
                            <div
                                className="absolute top-[380px] right-[-60px] w-[248px] bg-white rounded-2xl p-5 shadow-[0_12px_40px_-10px_rgba(251,146,60,0.25)] border border-zinc-100 z-20"
                                data-testid="evaluation-card"
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertTriangle className="w-5 h-5 text-orange-500 fill-orange-100" strokeWidth={2.5} />
                                    <span className="text-[13px] font-bold text-orange-500">Evaluation needed</span>
                                </div>
                                <p className="text-[13px] text-zinc-600 leading-snug">
                                    Consider counter
                                    <br />
                                    arguments and
                                    <br />
                                    balance.
                                </p>
                                {/* Anchor dot on the left */}
                                <span className="absolute -left-1.5 top-1/2 w-3 h-3 bg-orange-500 rounded-full border-2 border-white" />
                            </div>

                            {/* IMPROVE – bottom left */}
                            <div
                                className="absolute bottom-[48px] left-[60px] w-[256px] bg-white rounded-2xl p-5 shadow-[0_12px_40px_-10px_rgba(124,58,237,0.3)] border border-zinc-100 z-20"
                                data-testid="improve-card"
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-4 h-4 rounded-full bg-[#7C3AED]" />
                                    <span className="text-[13px] font-bold text-[#7C3AED]">Improve</span>
                                </div>
                                <p className="text-[19px] font-black text-zinc-950 mb-1">Stronger conclusion</p>
                                <p className="text-[13px] text-zinc-500">Link back to context.</p>
                                {/* Anchor dot on top-right */}
                                <span className="absolute -right-1.5 top-8 w-3 h-3 bg-[#7C3AED] rounded-full border-2 border-white" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <PricingSection embedded />
        </div>
    );
}
